"use client";
import { useEffect, useState } from "react";
import PricingCard, { TPricingCard } from "./PricingCard";
import * as S from "./styles.ts";
import { H1, P, StyledLink } from "@template/components";
import UserCount from "@template/blocks/PricingSection/component/UserCount";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import { getPlanPrices } from "@/redux/slices/store/thunks/getPlanPrices.ts";

interface TPricingSectionComponentProps {
  heading?: string;
  footer?: string;
  countOptions: {
    defaultValue: number;
    showInput?: boolean;
    showSlider?: boolean;
  };
}

const usePricingCards = () => {
  const currency = useCurrency();

  const cards: Partial<TPricingCard>[] = [
    {
      footer: (
        <>
          + many <span>More Features</span>
        </>
      ),
      pills: [
        {
          children: "Call Routing",
        },
        {
          children: "Desktop & Mobile Apps",
        },
      ],

      copy: "Perfect for startups and those looking for business phone numbers & service",
      features: ["100 Minutes Included Per User"],
      list: [
        {
          label: "Call Routing",
        },
        {
          label: "Desktop & Mobile Apps",
        },
        {
          label: "Unlimited Inbound Calls",
        },
        {
          label: "Call Routing & Forwarding",
        },
        {
          label: "Time of Day Call Handling",
        },
        {
          label: "Unlimited Voicemail",
        },
        {
          label: "Call Recording",
        },
        {
          label: "Business Messaging",
        },
      ],
    },
    {
      footer: (
        <>
          + everything from <span>Core</span>
        </>
      ),
      pills: [
        {
          children: "IVR Call Menus",
        },
        {
          children: "Call Monitoring",
        },
      ],

      copy: "Our most popular plan, ideal for small offices with IVR call menus & more",
      features: ["750 Minutes Included Per User"],
      list: [
        {
          label: "Multi-Level IVR Call Menus",
        },
        {
          label: "Voicemail Transcription",
        },
        {
          label: "Call Monitoring",
        },
        {
          label: "CRM & App Integrations",
        },
        {
          label: "Phone Book Manager",
        },
        {
          label: "Click To Dial",
        },
        {
          label: "Caller ID Routing",
        },
        {
          label: "Custom Member Roles",
        },
      ],
    },
    {
      footer: (
        <>
          + Everything from <span>Core & Standard</span>
        </>
      ),
      copy: "Ideal for those needing unlimited minutes and international calls, with rich features",
      pills: [
        {
          children: "Call Queueing",
        },
        {
          children: "Wallboard & Reporting",
        },
      ],

      features: [
        `Unlimited Minutes (${currency === "$" ? "US" : "UK"} + 54 Countries)`,
      ],
      list: [
        {
          label: "Smart Call Queuing",
        },
        {
          label: "Live Call Analytics",
        },
        {
          label: "Unlimited Call Recording",
        },
        {
          label: "Text to Speech",
        },
        {
          label: "Scheduled Call Reporting",
        },
        {
          label: "Call Quality Feedback",
        },
        {
          label: "Cloud Storage Sync",
        },
        {
          label: "Bulk SMS",
        },
      ],
    },
  ];

  return cards;
};

const PricingSectionComponent = ({
                                   heading,
                                   footer,
                                   countOptions = {
                                     defaultValue: 2,
                                     showInput: true,
                                     showSlider: true,
                                   },
                                 }: TPricingSectionComponentProps) => {
  const dispatch = useTypedDispatch();
  const pricingCards = usePricingCards();

  const plans = useTypedSelector((state) => state.store.plan_prices.plans);
  const plansLoaded = useTypedSelector(
    (state) => state.store.plan_prices.loaded,
  );

  const [userCount, setUserCount] = useState(countOptions.defaultValue);

  const usedPlans = plans.map((plan, index) => ({
    ...pricingCards[index],
    type: plan.id - 1,
  }));

  const fakePlans = pricingCards.map((card, index) => ({
    ...card,
    type: index + 100,
  }));

  useEffect(() => {
    if (plansLoaded && !plans.length) {
      dispatch(getPlanPrices());
    }
  }, [plansLoaded]);

  return (
    <S.PricingSectionWrapper id="pricing-cards-section">
      <S.PricingSectionContainer>
        <S.PricingSectionHeader>
          {heading && <H1 as="h2">{heading}</H1>}
          <UserCount
            value={userCount}
            setValue={setUserCount}
            showInput={countOptions.showInput}
            showSlider={countOptions.showSlider}
          />
        </S.PricingSectionHeader>
        <S.PricingCardWrapper>
          <S.PricingCardContainer>
            {(plansLoaded ? usedPlans : fakePlans).map((card) => (
              <PricingCard
                {...card}
                key={card.type}
                userCount={userCount}
                loading={!plansLoaded}
              />
            ))}
          </S.PricingCardContainer>
        </S.PricingCardWrapper>

        <S.FooterWrapper>
          {footer && <P>{footer}</P>}
          <P>
            Still Not Sure?{" "}
            <StyledLink href="/voice">Compare our plans.</StyledLink>
          </P>
        </S.FooterWrapper>
      </S.PricingSectionContainer>
    </S.PricingSectionWrapper>
  );
};

export default PricingSectionComponent;

export const options = [
  {
    description: "",
    displayName: "PricingSectionComponent",
    methods: [],
    props: {
      heading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      footer: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
    },
  },
];
